// useLayoutEditorPreviewData.js

import { useEffect, useMemo, useState } from 'react';

// Services
import { subscribeToNode, subscribeToNodes } from 'services/nodes/NodeReadService';
import { subscribeToViews } from 'services/views/ViewReadService';

// Node
import { getDisplayType } from 'node/groups/groupChildNodesByType';

// Utilities
import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';
import { isRelationFieldType } from 'utilities/FieldTypes';
import { normalizeRollupDefinitions } from 'rollups/rollupDefinitions';
import { evaluateRollups } from 'rollups/rollupEvaluator';
import { createFilterSortEvaluator } from 'views/utils/filterSortEvaluator';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';

import { collectReferencedVideoNodeKeysFromBlocks } from './pageVideoBlockUtils';

function normalizeString(value) {
	return String(value || '').trim();
}

function getFieldKey(fieldDef) {
	return normalizeString(fieldDef?.name || fieldDef?.key);
}

function normalizeReferencedNodeKeys(value) {
	const list = Array.isArray(value) ? value : value == null ? [] : [value];
	const seen = new Set();

	return list
		.map((entry) => {
			if (typeof entry === 'string') return normalizeString(entry);
			if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
				return normalizeString(entry?.key || entry?.nodeKey || entry?.id);
			}

			return '';
		})
		.filter((entry) => {
			if (!entry || seen.has(entry)) return false;
			seen.add(entry);
			return true;
		});
}

function readPreviewNodeFieldValue({ fieldDef, node, typeKey }) {
	const fieldKey = getFieldKey(fieldDef);
	if (!fieldKey || !node) return undefined;

	if (node?.values && typeof node.values === 'object' && !Array.isArray(node.values)) {
		if (Object.prototype.hasOwnProperty.call(node.values, fieldKey)) {
			return node.values[fieldKey];
		}

		const nestedValue = Object.values(node.values).find((candidate) => {
			return candidate
				&& typeof candidate === 'object'
				&& !Array.isArray(candidate)
				&& Object.prototype.hasOwnProperty.call(candidate, fieldKey);
		});

		if (nestedValue) {
			return nestedValue[fieldKey];
		}
	}

	const flatFieldKey = `data_${fieldKey}`;
	if (Object.prototype.hasOwnProperty.call(node, flatFieldKey)) {
		return node[flatFieldKey];
	}

	return resolveSchemaFieldValue({
		item: node,
		fieldDef,
		typeKey,
		dataSection: '',
	});
}

function collectPreviewFieldNodeKeys({ blocksByKey, getType, previewNode }) {
	const previewTypeKey = normalizeString(previewNode?.type);
	const previewTypeDef = previewTypeKey ? getType(previewTypeKey) || null : null;
	const schemaFields = resolveDisplaySchemaFields(previewTypeDef, { includeHidden: true });

	if (!previewNode || !previewTypeKey || !schemaFields.length) {
		return [];
	}

	const selectedFieldKeys = new Set(
		Object.values(blocksByKey || {}).flatMap((block) => {
			const blockType = String(block?.type || '').trim().toUpperCase();
			if (blockType !== 'FIELDS' && blockType !== 'FIELD') {
				return [];
			}

			return (Array.isArray(block?.fieldKeys) ? block.fieldKeys : [])
				.map((fieldKey) => normalizeString(fieldKey))
				.filter(Boolean);
		}),
	);

	if (!selectedFieldKeys.size) {
		return [];
	}

	const referencedNodeKeys = new Set();

	schemaFields
		.filter((fieldDef) => (
			selectedFieldKeys.has(getFieldKey(fieldDef))
			&& isRelationFieldType(fieldDef)
		))
		.forEach((fieldDef) => {
			const rawValue = readPreviewNodeFieldValue({
				fieldDef,
				node: previewNode,
				typeKey: previewTypeKey,
			});

			normalizeReferencedNodeKeys(rawValue).forEach((nodeKey) => {
				referencedNodeKeys.add(nodeKey);
			});
		});

	return Array.from(referencedNodeKeys);
}

function getViewKey(view) {
	return String(view?.key || view?.id || '').trim();
}

function getViewTypeKey(view) {
	return String(view?.nodeType || view?.typeKey || '').trim();
}

function getViewBlockTypeKey(block) {
	return String(block?.typeKey || block?.view?.nodeType || block?.view?.typeKey || '').trim();
}

function getViewBlockRelatedTypeKey(block, view = null) {
	return String(block?.relatedTypeKey || view?.nodeType || view?.nodeTypeKey || view?.typeKey || '').trim();
}

function filterNodesByType(sourceNodes = [], typeKey = '') {
	if (!typeKey) return [];

	return (Array.isArray(sourceNodes) ? sourceNodes : []).filter((item) => getDisplayType(item) === typeKey);
}

function deriveViewNodes({ sourceNodes = [], typeDef, typeKey, view, viewerUserKey = null }) {
	if (!view || !typeKey) return [];

	const resolvedFields = resolveDisplaySchemaFields(typeDef || { key: typeKey }, { includeHidden: true });
	const schemaFieldMap = new Map(
		(Array.isArray(resolvedFields) ? resolvedFields : []).map((field) => [field?.name || field?.key, field])
	);

	const resolveFieldValue = (item, fieldDef) => (
		resolveSchemaFieldValue({
			item,
			fieldDef,
			typeKey,
			dataSection: typeDef?.dataSection,
		})
	);

	const { applyFilters, applySorts } = createFilterSortEvaluator({
		resolveFieldValue,
		schemaFieldMap,
		viewerUserKey,
	});

	const viewFilters = Array.isArray(view?.filters) ? view.filters : [];
	const viewSorts = Array.isArray(view?.sorts) ? view.sorts : [];
	const filteredNodes = viewFilters.length
		? sourceNodes.filter((item) => applyFilters(item, viewFilters))
		: sourceNodes;

	return viewSorts.length ? applySorts(filteredNodes, viewSorts) : filteredNodes;
}

function getTypeFields(typeKey, typeDef) {
	return resolveDisplaySchemaFields(typeDef || { key: typeKey }, { includeHidden: true });
}

function evaluateSingleRollup({ items = [], rollup, typeDef, typeKey }) {
	if (!rollup || !typeKey) return null;

	const fields = getTypeFields(typeKey, typeDef);
	const results = evaluateRollups({
		fields,
		items,
		rollups: [rollup],
		resolveFieldValue: (item, fieldDef) => resolveSchemaFieldValue({
			item,
			fieldDef,
			typeKey,
			dataSection: typeDef?.dataSection,
		}),
	});

	return Array.isArray(results) ? results[0] || null : null;
}

export default function useLayoutEditorPreviewData({
	blocksByKey,
	getType,
	previewNode = null,
	previewNodeChildNodes = [],
	previewNodeDescendantNodes = [],
	userKey = null,
	baseKey,
}) {
	const [pageNodesByKey, setPageNodesByKey] = useState({});
	const [pageNodesByType, setPageNodesByType] = useState({});
	const [pageViews, setPageViews] = useState([]);

	const referencedViewBlocks = useMemo(() => {
		return Object.entries(blocksByKey || {}).reduce((blocks, [blockKey, block]) => {
			if (String(block?.type || '').trim().toUpperCase() !== 'VIEW') {
				return blocks;
			}

			const cleanedBlockKey = String(blockKey || '').trim();
			if (!cleanedBlockKey) {
				return blocks;
			}

			blocks.push({
				block,
				blockKey: cleanedBlockKey,
			});
			return blocks;
		}, []);
	}, [blocksByKey]);
	const referencedViewIds = useMemo(() => {
		return Array.from(new Set(
			referencedViewBlocks
				.map(({ block }) => {
					if (block?.view && typeof block.view === 'object') return '';
					return String(block?.viewId || '').trim();
				})
				.filter(Boolean)
		));
	}, [referencedViewBlocks]);
	const referencedNodeKeys = useMemo(() => {
		return Array.from(new Set(
			[
				...collectReferencedVideoNodeKeysFromBlocks(blocksByKey),
				...collectPreviewFieldNodeKeys({
					blocksByKey,
					getType,
					previewNode,
				}),
			]
		));
	}, [blocksByKey, getType, previewNode]);
	const referencedRollupSelections = useMemo(() => {
		return Object.values(blocksByKey || {})
			.map((block) => {
				if (String(block?.type || '').trim().toUpperCase() !== 'ROLLUP') return null;

				const rollupKey = String(block?.rollupKey || '').trim();
				const typeKey = String(block?.typeKey || '').trim();
				if (!rollupKey || !typeKey) return null;

				return {
					rollupKey,
					typeKey,
				};
			})
			.filter(Boolean);
	}, [blocksByKey]);
	const viewById = useMemo(() => {
		const map = new Map();

		pageViews.forEach((view) => {
			const viewKey = getViewKey(view);
			if (!viewKey) return;
			map.set(viewKey, view);
		});

		return map;
	}, [pageViews]);
	const nodeByKey = useMemo(() => {
		return pageNodesByKey && typeof pageNodesByKey === 'object'
			? pageNodesByKey
			: {};
	}, [pageNodesByKey]);
	const referencedTypeKeys = useMemo(() => {
		return Array.from(new Set(
			[
				...referencedViewBlocks.map(({ block }) => {
					if (block?.view && typeof block.view === 'object') {
						return getViewBlockTypeKey(block);
					}

					return '';
				}),
				...referencedRollupSelections.map((selection) => selection.typeKey),
			]
				.filter(Boolean)
		));
	}, [referencedRollupSelections, referencedViewBlocks]);
	const viewPreviewByBlockKey = useMemo(() => {
		const map = new Map();

		referencedViewBlocks.forEach(({ block, blockKey }) => {
			const isLocalViewDefinition = block?.view && typeof block.view === 'object';
			const view = isLocalViewDefinition
				? block.view
				: viewById.get(String(block?.viewId || '').trim());
			const typeKey = isLocalViewDefinition
				? getViewBlockTypeKey(block)
				: getViewBlockRelatedTypeKey(block, view) || getViewTypeKey(view);
			if (!view || !typeKey) return;

			const typeDef = getType(typeKey);
			const sourceNodes = filterNodesByType(
				Boolean(view?.includeDescendants) ? previewNodeDescendantNodes : previewNodeChildNodes,
				typeKey
			);

			map.set(blockKey, {
				nodes: deriveViewNodes({
					sourceNodes,
					typeDef,
					typeKey,
					view,
					viewerUserKey: userKey,
				}),
				typeDef,
				view,
			});
		});

		return map;
	}, [getType, previewNodeChildNodes, previewNodeDescendantNodes, referencedViewBlocks, userKey, viewById]);
	const rollupPreviewBySelectionKey = useMemo(() => {
		const map = new Map();

		referencedRollupSelections.forEach((selection) => {
			const typeKey = String(selection?.typeKey || '').trim();
			const rollupKey = String(selection?.rollupKey || '').trim();
			if (!typeKey || !rollupKey) return;

			const typeDef = getType(typeKey) || null;
			const normalizedRollups = normalizeRollupDefinitions(typeDef?.rollups, { typeKey });
			const rollup = normalizedRollups.find((entry) => String(entry?.key || '').trim() === rollupKey) || null;
			const items = Array.isArray(pageNodesByType[typeKey]) ? pageNodesByType[typeKey] : [];
			const result = evaluateSingleRollup({
				items,
				rollup,
				typeDef,
				typeKey,
			});

			map.set(`${typeKey}:${rollupKey}`, {
				result,
				rollup,
				typeDef,
			});
		});

		return map;
	}, [getType, pageNodesByType, referencedRollupSelections]);

	useEffect(() => {
		if (!baseKey || !referencedViewIds.length) {
			setPageViews([]);
			return undefined;
		}

		return subscribeToViews({ baseKey }, (rows) => {
			setPageViews(Array.isArray(rows) ? rows : []);
		});
	}, [referencedViewIds.length, baseKey]);

	useEffect(() => {
		if (!referencedNodeKeys.length) {
			setPageNodesByKey({});
			return undefined;
		}

		const rowsByKey = {};

		const emit = () => {
			setPageNodesByKey({ ...rowsByKey });
		};

		const unsubscribes = referencedNodeKeys.map((nodeKey) => subscribeToNode(nodeKey, (node) => {
			const cleanedNodeKey = normalizeString(nodeKey);
			if (!cleanedNodeKey) {
				return;
			}

			if (node?.key) {
				rowsByKey[cleanedNodeKey] = node;
			} else {
				delete rowsByKey[cleanedNodeKey];
			}

			emit();
		}));

		return () => {
			unsubscribes.forEach((unsubscribe) => unsubscribe?.());
		};
	}, [referencedNodeKeys]);

	useEffect(() => {
		if (!baseKey || !referencedTypeKeys.length) {
			setPageNodesByType({});
			return undefined;
		}

		setPageNodesByType({});

		const unsubscribes = referencedTypeKeys.map((typeKey) => {
			return subscribeToNodes({
				params: [
					{
						field: 'baseKey',
						operator: '==',
						value: baseKey,
					},
					{
						field: 'type',
						operator: '==',
						value: typeKey,
					},
				],
			}, (rows) => {
				setPageNodesByType((current) => ({
					...current,
					[typeKey]: Array.isArray(rows) ? rows : [],
				}));
			});
		});

		return () => {
			unsubscribes.forEach((unsubscribe) => {
				unsubscribe?.();
			});
		};
	}, [referencedTypeKeys, baseKey]);

	return {
		nodeByKey,
		nodesByType: pageNodesByType,
		rollupPreviewBySelectionKey,
		viewPreviewByBlockKey,
	};
}